import type {
  ForexVendor,
  ForexListing,
  ForexReview,
  ForexReport,
  ForexFilters,
} from "@/store/slices/forexSlice";
import { getForexData, nigerianCities } from "./forexData";

// Simulate API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Get current user from auth
const getCurrentUser = () => {
  if (typeof window === "undefined") return null;
  const user = localStorage.getItem("nexara_user");
  return user ? JSON.parse(user) : null;
};

export const forexApi = {
  // Vendors
  getVendors: async (): Promise<ForexVendor[]> => {
    await delay(500);
    const { vendors } = getForexData();
    return vendors.filter((v: ForexVendor) => v.status === "active");
  },

  getVendorById: async (id: string): Promise<ForexVendor | null> => {
    await delay(300);
    const { vendors } = getForexData();
    return vendors.find((v: ForexVendor) => v.id === id) || null;
  },

  updateVendor: async (
    id: string,
    data: Partial<ForexVendor>,
  ): Promise<ForexVendor> => {
    await delay(500);
    const { vendors } = getForexData();
    const index = vendors.findIndex((v: ForexVendor) => v.id === id);
    if (index === -1) throw new Error("Vendor not found");

    vendors[index] = { ...vendors[index], ...data };
    localStorage.setItem("forex_vendors", JSON.stringify(vendors));
    return vendors[index];
  },

  // Listings
  getListings: async (
    filters?: Partial<ForexFilters>,
  ): Promise<ForexListing[]> => {
    await delay(600);
    const { listings } = getForexData();
    let result = listings.filter((l: ForexListing) => l.status === "active");

    if (filters) {
      // Location filter
      if (filters.city) {
        result = result.filter((l: ForexListing) =>
          l.location.city.toLowerCase().includes(filters.city!.toLowerCase()),
        );
      }
      if (filters.state) {
        result = result.filter((l: ForexListing) =>
          l.location.state.toLowerCase().includes(filters.state!.toLowerCase()),
        );
      }

      // Currency filter
      if (filters.currencyFrom) {
        result = result.filter((l: ForexListing) =>
          l.currencies.some(
            (c: { from: string }) => c.from === filters.currencyFrom,
          ),
        );
      }
      if (filters.currencyTo) {
        result = result.filter((l: ForexListing) =>
          l.currencies.some((c: { to: string }) => c.to === filters.currencyTo),
        );
      }

      // Rate filter
      if (filters.minRate) {
        result = result.filter((l: ForexListing) =>
          l.currencies.some(
            (c: { sellRate: number }) => c.sellRate >= filters.minRate!,
          ),
        );
      }
      if (filters.maxRate) {
        result = result.filter((l: ForexListing) =>
          l.currencies.some(
            (c: { buyRate: number }) => c.buyRate <= filters.maxRate!,
          ),
        );
      }

      // Verified only
      if (filters.verifiedOnly) {
        const { vendors } = getForexData();
        result = result.filter((l: ForexListing) => {
          const vendor = vendors.find((v: ForexVendor) => v.id === l.vendorId);
          return vendor?.isVerified;
        });
      }

      // Sorting
      switch (filters.sortBy) {
        case "best_rate":
          result.sort((a: ForexListing, b: ForexListing) => {
            const aRate = Math.min(
              ...a.currencies.map((c: { sellRate: number }) => c.sellRate),
            );
            const bRate = Math.min(
              ...b.currencies.map((c: { sellRate: number }) => c.sellRate),
            );
            return aRate - bRate;
          });
          break;
        case "newest":
          result.sort(
            (a: ForexListing, b: ForexListing) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
          break;
        case "most_reviewed":
          const { vendors } = getForexData();
          result.sort((a: ForexListing, b: ForexListing) => {
            const aVendor = vendors.find(
              (v: ForexVendor) => v.id === a.vendorId,
            );
            const bVendor = vendors.find(
              (v: ForexVendor) => v.id === b.vendorId,
            );
            return (bVendor?.reviewCount || 0) - (aVendor?.reviewCount || 0);
          });
          break;
      }
    }

    return result;
  },

  getFeaturedListings: async (): Promise<ForexListing[]> => {
    await delay(400);
    const { listings } = getForexData();
    return listings
      .filter((l: ForexListing) => l.isFeatured && l.status === "active")
      .slice(0, 8);
  },

  getListingById: async (id: string): Promise<ForexListing | null> => {
    await delay(300);
    const { listings } = getForexData();
    return listings.find((l: ForexListing) => l.id === id) || null;
  },

  getVendorListings: async (vendorId: string): Promise<ForexListing[]> => {
    await delay(400);
    const { listings } = getForexData();
    return listings.filter((l: ForexListing) => l.vendorId === vendorId);
  },

  createListing: async (data: Partial<ForexListing>): Promise<ForexListing> => {
    await delay(800);
    const { listings, vendors } = getForexData();
    const currentUser = getCurrentUser();

    const vendor = vendors.find(
      (v: ForexVendor) => v.email === currentUser?.email,
    );
    if (!vendor) throw new Error("Vendor profile not found");

    const newListing: ForexListing = {
      id: `listing_${Date.now()}`,
      vendorId: vendor.id,
      title: data.title || "",
      description: data.description || "",
      currencies: data.currencies || [],
      location: data.location || vendor.location,
      images: data.images || [],
      isFeatured: false,
      isPromoted: false,
      status: "pending",
      views: 0,
      clicks: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    };

    listings.unshift(newListing);
    localStorage.setItem("forex_listings", JSON.stringify(listings));
    return newListing;
  },

  updateListing: async (
    id: string,
    data: Partial<ForexListing>,
  ): Promise<ForexListing> => {
    await delay(600);
    const { listings } = getForexData();
    const index = listings.findIndex((l: ForexListing) => l.id === id);
    if (index === -1) throw new Error("Listing not found");

    listings[index] = {
      ...listings[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem("forex_listings", JSON.stringify(listings));
    return listings[index];
  },

  deleteListing: async (id: string): Promise<void> => {
    await delay(400);
    const { listings } = getForexData();
    const filtered = listings.filter((l: ForexListing) => l.id !== id);
    localStorage.setItem("forex_listings", JSON.stringify(filtered));
  },

  // Analytics
  recordView: async (id: string): Promise<void> => {
    await delay(100);
    const { listings, vendors } = getForexData();
    const listingIndex = listings.findIndex((l: ForexListing) => l.id === id);

    if (listingIndex !== -1) {
      listings[listingIndex].views += 1;
      localStorage.setItem("forex_listings", JSON.stringify(listings));

      // Also update vendor total views
      const vendorIndex = vendors.findIndex(
        (v: ForexVendor) => v.id === listings[listingIndex].vendorId,
      );
      if (vendorIndex !== -1) {
        vendors[vendorIndex].totalViews += 1;
        localStorage.setItem("forex_vendors", JSON.stringify(vendors));
      }
    }
  },

  recordClick: async (id: string): Promise<void> => {
    await delay(100);
    const { listings, vendors } = getForexData();
    const listingIndex = listings.findIndex((l: ForexListing) => l.id === id);

    if (listingIndex !== -1) {
      listings[listingIndex].clicks += 1;
      localStorage.setItem("forex_listings", JSON.stringify(listings));

      // Also update vendor total clicks
      const vendorIndex = vendors.findIndex(
        (v: ForexVendor) => v.id === listings[listingIndex].vendorId,
      );
      if (vendorIndex !== -1) {
        vendors[vendorIndex].totalClicks += 1;
        localStorage.setItem("forex_vendors", JSON.stringify(vendors));
      }
    }
  },

  // Reviews
  getListingReviews: async (listingId: string): Promise<ForexReview[]> => {
    await delay(400);
    const { reviews } = getForexData();
    return reviews.filter((r: ForexReview) => r.listingId === listingId);
  },

  submitReview: async (data: Partial<ForexReview>): Promise<ForexReview> => {
    await delay(600);
    const { reviews, vendors } = getForexData();

    const newReview: ForexReview = {
      id: `review_${Date.now()}`,
      listingId: data.listingId || "",
      vendorId: data.vendorId || "",
      reviewerName: data.reviewerName || "Anonymous",
      reviewerAvatar: data.reviewerAvatar,
      rating: data.rating || 5,
      comment: data.comment || "",
      createdAt: new Date().toISOString(),
      isVerified: false,
    };

    reviews.unshift(newReview);
    localStorage.setItem("forex_reviews", JSON.stringify(reviews));

    // Update vendor rating
    const vendorIndex = vendors.findIndex(
      (v: ForexVendor) => v.id === data.vendorId,
    );
    if (vendorIndex !== -1) {
      const vendorReviews = reviews.filter(
        (r: ForexReview) => r.vendorId === data.vendorId,
      );
      const avgRating =
        vendorReviews.reduce(
          (sum: number, r: ForexReview) => sum + r.rating,
          0,
        ) / vendorReviews.length;
      vendors[vendorIndex].rating = Math.round(avgRating * 10) / 10;
      vendors[vendorIndex].reviewCount = vendorReviews.length;
      localStorage.setItem("forex_vendors", JSON.stringify(vendors));
    }

    return newReview;
  },

  // Reports
  getReports: async (): Promise<ForexReport[]> => {
    await delay(400);
    const { reports } = getForexData();
    return reports;
  },

  reportListing: async (data: Partial<ForexReport>): Promise<ForexReport> => {
    await delay(500);
    const { reports } = getForexData();

    const newReport: ForexReport = {
      id: `report_${Date.now()}`,
      listingId: data.listingId || "",
      vendorId: data.vendorId || "",
      reporterName: data.reporterName || "Anonymous",
      reporterPhone: data.reporterPhone || "",
      reason: data.reason || "",
      details: data.details || "",
      status: "pending",
      createdAt: new Date().toISOString(),
    };

    reports.unshift(newReport);
    localStorage.setItem("forex_reports", JSON.stringify(reports));
    return newReport;
  },

  updateReportStatus: async (
    id: string,
    status: ForexReport["status"],
  ): Promise<ForexReport> => {
    await delay(400);
    const { reports } = getForexData();
    const index = reports.findIndex((r: ForexReport) => r.id === id);
    if (index === -1) throw new Error("Report not found");

    reports[index].status = status;
    localStorage.setItem("forex_reports", JSON.stringify(reports));
    return reports[index];
  },

  // Vendor Dashboard Stats
  getVendorDashboardStats: async (vendorId: string) => {
    await delay(500);
    const { listings, vendors, reviews } = getForexData();

    const vendor = vendors.find((v: ForexVendor) => v.id === vendorId);
    const vendorListings = listings.filter(
      (l: ForexListing) => l.vendorId === vendorId,
    );
    const vendorReviews = reviews.filter(
      (r: ForexReview) => r.vendorId === vendorId,
    );

    // Generate weekly stats
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const weeklyStats = days.map((day) => ({
      day,
      views: Math.floor(Math.random() * 100) + 20,
      clicks: Math.floor(Math.random() * 50) + 10,
    }));

    return {
      totalViews: vendor?.totalViews || 0,
      totalClicks: vendor?.totalClicks || 0,
      activeListings: vendorListings.filter(
        (l: ForexListing) => l.status === "active",
      ).length,
      totalReviews: vendorReviews.length,
      averageRating: vendor?.rating || 0,
      weeklyStats,
    };
  },

  // Admin functions
  approveListing: async (id: string): Promise<ForexListing> => {
    await delay(400);
    const { listings } = getForexData();
    const index = listings.findIndex((l: ForexListing) => l.id === id);
    if (index === -1) throw new Error("Listing not found");

    listings[index].status = "active";
    localStorage.setItem("forex_listings", JSON.stringify(listings));
    return listings[index];
  },

  rejectListing: async (id: string): Promise<ForexListing> => {
    await delay(400);
    const { listings } = getForexData();
    const index = listings.findIndex((l: ForexListing) => l.id === id);
    if (index === -1) throw new Error("Listing not found");

    listings[index].status = "rejected";
    localStorage.setItem("forex_listings", JSON.stringify(listings));
    return listings[index];
  },

  verifyVendor: async (
    id: string,
    badge: "basic" | "premium",
  ): Promise<ForexVendor> => {
    await delay(400);
    const { vendors } = getForexData();
    const index = vendors.findIndex((v: ForexVendor) => v.id === id);
    if (index === -1) throw new Error("Vendor not found");

    vendors[index].isVerified = true;
    vendors[index].verificationBadge = badge;
    localStorage.setItem("forex_vendors", JSON.stringify(vendors));
    return vendors[index];
  },

  suspendVendor: async (id: string): Promise<ForexVendor> => {
    await delay(400);
    const { vendors } = getForexData();
    const index = vendors.findIndex((v: ForexVendor) => v.id === id);
    if (index === -1) throw new Error("Vendor not found");

    vendors[index].status = "suspended";
    localStorage.setItem("forex_vendors", JSON.stringify(vendors));
    return vendors[index];
  },

  // Location search
  searchLocations: async (query: string): Promise<typeof nigerianCities> => {
    await delay(200);
    return nigerianCities.filter(
      (city) =>
        city.name.toLowerCase().includes(query.toLowerCase()) ||
        city.state.toLowerCase().includes(query.toLowerCase()),
    );
  },

  getNearbyListings: async (
    lat: number,
    lng: number,
    radius: number = 10,
  ): Promise<ForexListing[]> => {
    await delay(500);
    const { listings } = getForexData();

    return listings.filter((l: ForexListing) => {
      const distance = Math.sqrt(
        Math.pow(l.location.coordinates.lat - lat, 2) +
          Math.pow(l.location.coordinates.lng - lng, 2),
      );
      return distance <= radius / 111; // Convert km to degrees
    });
  },
};
